import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, ScatterChart, Scatter, Label,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Sankey
} from 'recharts';
import ItemsChartsSection from './ItemsChartsSection';
import OrdersChartsSection from './OrdersChartsSection';


const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDate = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, d] = String(isoDate).slice(0, 10).split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return String(isoDate);
  if (m < 1 || m > 12) return String(isoDate);
  return `${MONTHS[m - 1]} ${d}`;
};

const formatMonthDay = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, d] = String(isoDate).slice(0, 10).split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return String(isoDate);
  if (m < 1 || m > 12) return String(isoDate);
  return `${MONTHS[m - 1]} ${d}`;
};

const formatHour12 = (hour) => {
  const h = Number(hour);
  if (!Number.isFinite(h) || h < 0 || h > 23) return '';
  const suffix = h < 12 ? 'am' : 'pm';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:00${suffix}`;
};

const buildTrendTicks = (rows = []) => {
  const dates = rows
    .map((r) => String(r?.date || '').slice(0, 10))
    .filter(Boolean);
  if (!dates.length) return [];
  if (dates.length <= 6) return dates;

  const step = Math.ceil(dates.length / 6);
  const ticks = [];
  for (let i = 0; i < dates.length; i += step) {
    ticks.push(dates[i]);
  }
  if (ticks[ticks.length - 1] !== dates[dates.length - 1]) {
    ticks.push(dates[dates.length - 1]);
  }
  return ticks;
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_COLORS = ['#2563EB', '#7C3AED', '#0EA5E9', '#16A34A', '#F59E0B', '#EF4444', '#6B7280'];
const PIE_COLORS = ['#2563EB', '#7C3AED', '#0EA5E9', '#16A34A', '#F59E0B', '#EF4444', '#6B7280', '#10B981', '#9333EA', '#DB2777', '#0891B2', '#65A30D', '#4B5563'];
const MAX_PIE_SLICES = 6;
const DAYPART_CONFIG = [
  { key: 'Breakfast', color: '#2563EB' },
  { key: 'Lunch', color: '#16A34A' },
  { key: 'Dinner', color: '#F59E0B' },
  { key: 'Late Night', color: '#7C3AED' },
];
const DAYPART_WINDOWS = {
  Breakfast: '5:00 AM - 11:00 AM',
  Lunch: '11:00 AM - 4:00 PM',
  Dinner: '4:00 PM - 10:00 PM',
  'Late Night': '10:00 PM - 5:00 AM',
};
const SANKEY_LEVEL_COLORS = {
  source: '#2563EB',
  category: '#16A34A',
  sublocation: '#F59E0B',
  screen: '#7C3AED',
};
const SANKEY_LEVEL_LEGEND = [
  { key: 'source', label: 'Order Source' },
  { key: 'category', label: 'Category' },
  { key: 'sublocation', label: 'Sublocation' },
  { key: 'screen', label: 'Screen' },
];
const CARD_CLASS = 'bg-white border border-gray-200 rounded-lg p-4 shadow-sm dark:bg-gray-900 dark:border-gray-700';
const TITLE_CLASS = 'text-sm text-blue-700 dark:text-blue-300 font-medium mb-2';
const EMPTY_CLASS = 'text-sm text-gray-500 dark:text-gray-400';

const toWeekStartYmd = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(`${String(isoDate).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  const weekdayMon0 = (d.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  d.setUTCDate(d.getUTCDate() - weekdayMon0);
  return d.toISOString().slice(0, 10);
};

const weekdayFromIso = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(`${String(isoDate).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  const weekdayMon0 = (d.getUTCDay() + 6) % 7;
  return WEEKDAYS[weekdayMon0];
};

const ChartsSection = ({
  activeTab,
  ordersByDay = [],
  avgBumpTimeByDay = [],
  ordersHeatmap = [],
  itemsData = [],
  menuCategoryDaypartData = [],
  sourceCategoryScreenFlow = [],
}) => {
  const trendTicks = useMemo(() => buildTrendTicks(ordersByDay), [ordersByDay]);
  const bumpTrendTicks = useMemo(() => buildTrendTicks(avgBumpTimeByDay), [avgBumpTimeByDay]);
  const overlayData = useMemo(() => {
    const map = new Map();

    for (const row of ordersByDay || []) {
      const date = String(row?.date || '').slice(0, 10);
      if (!date) continue;
      if (!map.has(date)) map.set(date, { date });
      map.get(date).total_orders = Number(row?.total_orders || 0);
    }

    for (const row of avgBumpTimeByDay || []) {
      const date = String(row?.date || '').slice(0, 10);
      if (!date) continue;
      if (!map.has(date)) map.set(date, { date });
      map.get(date).avg_bump_time_minutes = Number(row?.avg_bump_time_minutes || 0);
    }

    return Array.from(map.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }, [ordersByDay, avgBumpTimeByDay]);
  const overlayTicks = useMemo(() => buildTrendTicks(overlayData), [overlayData]);
  const heatmapStats = useMemo(() => {
    const keyToCount = new Map();
    let max = 0;

    for (const row of ordersHeatmap || []) {
      const weekday = Number(row?.weekday_index);
      const hour = Number(row?.hour_of_day);
      const count = Number(row?.total_orders || 0);
      if (!Number.isFinite(weekday) || !Number.isFinite(hour)) continue;
      if (weekday < 0 || weekday > 6 || hour < 0 || hour > 23) continue;
      keyToCount.set(`${weekday}-${hour}`, count);
      if (count > max) max = count;
    }

    return { keyToCount, max };
  }, [ordersHeatmap]);
  const heatColor = (count) => {
    if (!count || !heatmapStats.max) return 'rgba(59, 130, 246, 0.06)';
    const intensity = count / heatmapStats.max;
    const alpha = 0.15 + (0.85 * intensity);
    return `rgba(37, 99, 235, ${alpha.toFixed(3)})`;
  };
  const dayOfWeekTrendData = useMemo(() => {
    const weekMap = new Map();

    for (const row of ordersByDay || []) {
      const date = String(row?.date || '').slice(0, 10);
      const weekStart = toWeekStartYmd(date);
      const weekday = weekdayFromIso(date);
      if (!weekStart || !weekday) continue;

      if (!weekMap.has(weekStart)) {
        weekMap.set(weekStart, {
          week_start: weekStart,
          Mon: 0,
          Tue: 0,
          Wed: 0,
          Thu: 0,
          Fri: 0,
          Sat: 0,
          Sun: 0,
        });
      }
      weekMap.get(weekStart)[weekday] += Number(row?.total_orders || 0);
    }

    return Array.from(weekMap.values()).sort((a, b) =>
      String(a.week_start).localeCompare(String(b.week_start))
    );
  }, [ordersByDay]);
  const dayOfWeekTicks = useMemo(() => {
    const rows = dayOfWeekTrendData.map((d) => ({ date: d.week_start }));
    return buildTrendTicks(rows);
  }, [dayOfWeekTrendData]);
  const pieData = useMemo(() => {
    const base = (itemsData || [])
      .map((it) => ({
        name: String(it?.name || 'Unknown Item'),
        value: Number(it?.total_quantity || 0),
      }))
      .filter((it) => it.value > 0)
      .sort((a, b) => b.value - a.value);

    const total = base.reduce((sum, it) => sum + it.value, 0);
    if (!total) return [];

    if (base.length <= MAX_PIE_SLICES) {
      return base.map((item) => ({
        ...item,
        percent: (item.value / total) * 100,
      }));
    }

    const visibleItems = base.slice(0, MAX_PIE_SLICES - 1);
    const otherTotal = base
      .slice(MAX_PIE_SLICES - 1)
      .reduce((sum, item) => sum + item.value, 0);

    const result = visibleItems.map((item) => ({
      ...item,
      percent: (item.value / total) * 100,
    }));

    if (otherTotal > 0) {
      result.push({
        name: 'Other Items',
        value: otherTotal,
        percent: (otherTotal / total) * 100,
      });
    }

    return result;
  }, [itemsData]);
  const scatterData = useMemo(() => {
    return (itemsData || [])
      .map((it) => ({
        name: String(it?.name || 'Unknown Item'),
        total_quantity: Number(it?.total_quantity || 0),
        avg_bump_minutes: Number(it?.average_bump_time_seconds || 0) / 60,
      }))
      .filter((it) =>
        Number.isFinite(it.total_quantity) &&
        it.total_quantity > 0 &&
        Number.isFinite(it.avg_bump_minutes) &&
        it.avg_bump_minutes >= 0
      );
  }, [itemsData]);
  const categoryDaypartRadarData = useMemo(() => {
    const categoryMap = new Map();

    for (const row of menuCategoryDaypartData || []) {
      const category = String(row?.category_name || 'Uncategorized').trim() || 'Uncategorized';
      if (!categoryMap.has(category)) {
        categoryMap.set(category, {
          category,
          Breakfast: 0,
          Lunch: 0,
          Dinner: 0,
          'Late Night': 0,
          Breakfast_qty: 0,
          Lunch_qty: 0,
          Dinner_qty: 0,
          'Late Night_qty': 0,
        });
      }

      const daypart = String(row?.daypart || '');
      if (!DAYPART_CONFIG.some((d) => d.key === daypart)) continue;

      const percent = Number(row?.daypart_percent || 0);
      const qty = Number(row?.total_quantity || 0);
      const current = categoryMap.get(category);
      current[daypart] = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
      current[`${daypart}_qty`] = Number.isFinite(qty) ? qty : 0;
    }

    return Array.from(categoryMap.values())
      .sort((a, b) => {
        const aTotal = DAYPART_CONFIG.reduce((sum, d) => sum + Number(a[`${d.key}_qty`] || 0), 0);
        const bTotal = DAYPART_CONFIG.reduce((sum, d) => sum + Number(b[`${d.key}_qty`] || 0), 0);
        return bTotal - aTotal;
      })
      .slice(0, 6);
  }, [menuCategoryDaypartData]);
  const sankeyData = useMemo(() => {
    const nodeKeyToIndex = new Map();
    const nodes = [];
    const linksMap = new Map();

    const ensureNode = (key, label, level) => {
      if (nodeKeyToIndex.has(key)) return nodeKeyToIndex.get(key);
      const idx = nodes.length;
      nodes.push({
        name: label,
        level,
        fill: SANKEY_LEVEL_COLORS[level] || '#64748B',
      });
      nodeKeyToIndex.set(key, idx);
      return idx;
    };

    const addLink = (sourceIdx, targetIdx, value) => {
      if (!Number.isFinite(value) || value <= 0) return;
      const linkKey = `${sourceIdx}->${targetIdx}`;
      linksMap.set(linkKey, (linksMap.get(linkKey) || 0) + value);
    };

    for (const row of sourceCategoryScreenFlow || []) {
      const sourceLabel = String(row?.order_source || 'Unknown Source').trim() || 'Unknown Source';
      const categoryLabel = String(row?.category_name || 'Uncategorized').trim() || 'Uncategorized';
      const sublocationLabel = String(row?.sublocation_name || 'Unknown Sublocation').trim() || 'Unknown Sublocation';
      const screenLabel = String(row?.screen_name || 'Unknown Screen').trim() || 'Unknown Screen';
      const qty = Number(row?.total_quantity || 0);
      if (!Number.isFinite(qty) || qty <= 0) continue;

      const sourceIdx = ensureNode(`source:${sourceLabel}`, sourceLabel, 'source');
      const categoryIdx = ensureNode(`category:${categoryLabel}`, categoryLabel, 'category');
      const sublocationIdx = ensureNode(`sublocation:${sublocationLabel}`, sublocationLabel, 'sublocation');
      const screenIdx = ensureNode(`screen:${sublocationLabel}:${screenLabel}`, screenLabel, 'screen');

      addLink(sourceIdx, categoryIdx, qty);
      addLink(categoryIdx, sublocationIdx, qty);
      addLink(sublocationIdx, screenIdx, qty);
    }

    const links = Array.from(linksMap.entries()).map(([k, value]) => {
      const [source, target] = k.split('->').map((n) => Number(n));
      return { source, target, value };
    });

    return { nodes, links };
  }, [sourceCategoryScreenFlow]);
  const renderSankeyTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0]?.payload || {};
    const nodeByIndex = (idx) => {
      const n = Number(idx);
      if (!Number.isFinite(n) || n < 0 || n >= sankeyData.nodes.length) return null;
      return sankeyData.nodes[n] || null;
    };
    const sourceName =
      (typeof p?.source === 'object' ? p?.source?.name : null) ||
      nodeByIndex(p?.source)?.name ||
      p?.sourceName ||
      null;
    const targetName =
      (typeof p?.target === 'object' ? p?.target?.name : null) ||
      nodeByIndex(p?.target)?.name ||
      p?.targetName ||
      null;
    const nodeName = p?.name || payload[0]?.name || null;
    const value = Number(p?.value ?? payload[0]?.value ?? 0);
    const hasLink = !!sourceName || !!targetName;
    return (
      <div className="bg-white border border-gray-200 dark:bg-gray-900 dark:border-gray-700 rounded px-3 py-2 shadow-sm text-xs">
        {hasLink ? (
          <div className="font-semibold text-gray-800 dark:text-gray-100">
            {sourceName || 'Unknown'} {'->'} {targetName || 'Unknown'}
          </div>
        ) : (
          <div className="font-semibold text-gray-800 dark:text-gray-100">{nodeName || 'Unknown'}</div>
        )}
        <div className="text-gray-700 dark:text-gray-300">Items: {value.toLocaleString()}</div>
      </div>
    );
  };
  const renderRadarTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="bg-white border border-gray-200 dark:bg-gray-900 dark:border-gray-700 rounded px-3 py-2 shadow-sm text-xs">
        <div className="font-semibold text-gray-800 dark:text-gray-100 mb-1">{label || 'Unknown Category'}</div>
        {payload.map((entry) => {
          const daypart = entry?.dataKey;
          const pct = Number(entry?.value || 0);
          const qtyKey = `${daypart}_qty`;
          const qty = Number(entry?.payload?.[qtyKey] || 0);
          return (
            <div key={String(daypart)} className="text-gray-700 dark:text-gray-300">
              {daypart}: {pct.toFixed(1)}% ({qty.toLocaleString()} items)
            </div>
          );
        })}
      </div>
    );
  };
  const renderRadarLegend = (props) => {
    const payload = props?.payload || [];
    return (
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700 dark:text-gray-300 mt-1">
        {payload.map((entry) => {
          const key = String(entry?.value || '');
          const window = DAYPART_WINDOWS[key] || '';
          return (
            <span
              key={key}
              className="inline-flex items-center gap-1 cursor-help"
              title={window ? `${key}: ${window}` : key}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: entry?.color || '#64748B' }}
              />
              <span>{key}</span>
            </span>
          );
        })}
      </div>
    );
  };
  const renderScatterTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0]?.payload || {};
    return (
      <div className="bg-white border border-gray-200 dark:bg-gray-900 dark:border-gray-700 rounded px-3 py-2 shadow-sm text-xs">
        <div className="font-semibold text-gray-800 dark:text-gray-100">{p.name || 'Unknown Item'}</div>
        <div className="text-gray-700 dark:text-gray-300">Times Ordered: {Number(p.total_quantity || 0).toLocaleString()}</div>
        <div className="text-gray-700 dark:text-gray-300">Avg Bump: {Number(p.avg_bump_minutes || 0).toFixed(1)} min</div>
      </div>
    );
  };
  const renderPieTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0]?.payload || {};
    const percent = Number(p.percent || 0);
    return (
      <div className="bg-white border border-gray-200 dark:bg-gray-900 dark:border-gray-700 rounded px-3 py-2 shadow-sm text-xs">
        <div className="font-semibold text-gray-800 dark:text-gray-100">{p.name || 'Unknown Item'}</div>
        <div className="text-gray-700 dark:text-gray-300">Times Ordered: {Number(p.value || 0).toLocaleString()}</div>
        <div className="text-gray-700 dark:text-gray-300">Share: {percent.toFixed(1)}%</div>
      </div>
    );
  };
  const renderPieLabel = ({ name, cx, cy, midAngle, outerRadius }) => {
    const RADIAN = Math.PI / 180;
    const radius = Number(outerRadius || 150) + 56;
    const x = Number(cx || 0) + radius * Math.cos(-midAngle * RADIAN);
    const y = Number(cy || 0) + radius * Math.sin(-midAngle * RADIAN);
    const textAnchor = x >= Number(cx || 0) ? "start" : "end";
    const raw = String(name || "");
    const oneLine = raw.length > 28 ? `${raw.slice(0, 28)}...` : raw;
    const dy = 4;

    return (
      <text
        x={x}
        y={y}
        className="fill-slate-700 dark:fill-slate-200"
        textAnchor={textAnchor}
        dominantBaseline="central"
        fontSize={11}
      >
        <tspan x={x} dy={dy}>{oneLine}</tspan>
      </text>
    );
  };
  const renderPieLabelLine = ({ cx, cy, midAngle, outerRadius }) => {
    const RADIAN = Math.PI / 180;
    const baseR = Number(outerRadius || 150);
    const x1 = Number(cx || 0) + (baseR + 6) * Math.cos(-midAngle * RADIAN);
    const y1 = Number(cy || 0) + (baseR + 6) * Math.sin(-midAngle * RADIAN);
    const x2 = Number(cx || 0) + (baseR + 34) * Math.cos(-midAngle * RADIAN);
    const y2 = Number(cy || 0) + (baseR + 34) * Math.sin(-midAngle * RADIAN);
    const rightSide = x2 >= Number(cx || 0);
    const x3 = x2 + (rightSide ? 22 : -22);
    const y3 = y2;
    return (
      <path
        d={`M${x1},${y1}L${x2},${y2}L${x3},${y3}`}
        fill="none"
        stroke="#64748B"
        strokeWidth={1}
      />
    );
  };
  const renderHeatCellTooltip = (day, hour, count) =>
    `${day} ${formatHour12(hour)} - ${Number(count || 0).toLocaleString()} orders`;

  // Orders tab placeholder
  if (activeTab === 'Orders') {
    return (
      <OrdersChartsSection />
    );
  }

  // Items tab
  if (activeTab === 'Items') {
    return (
      <ItemsChartsSection data={itemsData} />
    );
  }

  // Overview tab
  if (activeTab === 'Overview') {
    return (
      // Inside activeTab === 'Overview' return block
      <div className="grid grid-cols-1 gap-6 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className={CARD_CLASS}>
            <h4 className={TITLE_CLASS}>Category Performance by Meal Period</h4>
            {categoryDaypartRadarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <RadarChart data={categoryDaypartRadarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="category" />
                  <PolarRadiusAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={renderRadarTooltip} />
                  <Legend content={renderRadarLegend} />
                  {DAYPART_CONFIG.map((d) => (
                    <Radar
                      key={d.key}
                      name={d.key}
                      dataKey={d.key}
                      stroke={d.color}
                      fill={d.color}
                      fillOpacity={0.16}
                      strokeWidth={2}
                    />
                  ))}
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <p className={EMPTY_CLASS}>No category/daypart data available.</p>
            )}
          </div>

          <div className={CARD_CLASS}>
            <h4 className={TITLE_CLASS}>Order Source - Category - Sublocation - Screen Flow</h4>
            {sankeyData.nodes.length > 0 && sankeyData.links.length > 0 ? (
              <div className="relative">
                <div className="absolute top-2 left-2 z-10 bg-white/90 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-[10px] text-gray-700 dark:text-gray-300 flex flex-wrap gap-x-3 gap-y-1">
                  {SANKEY_LEVEL_LEGEND.map((entry) => (
                    <div key={entry.key} className="flex items-center gap-1">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: SANKEY_LEVEL_COLORS[entry.key] }}
                      />
                      <span>{entry.label}</span>
                    </div>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={320}>
                  <Sankey
                    data={sankeyData}
                    nodePadding={24}
                    node={{ stroke: '#334155', strokeWidth: 0.8 }}
                    link={{ stroke: '#94A3B8', strokeOpacity: 0.35 }}
                    margin={{ top: 34, right: 20, bottom: 10, left: 20 }}
                  >
                    <Tooltip content={renderSankeyTooltip} />
                  </Sankey>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className={EMPTY_CLASS}>No source/category/screen flow data available.</p>
            )}
          </div>
        </div>

        <div className={CARD_CLASS}>
          <h4 className={TITLE_CLASS}>Orders vs Avg. Bump Time</h4>
          {overlayData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={overlayData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  ticks={overlayTicks}
                  tickFormatter={formatMonthDay}
                  minTickGap={30}
                />
                <YAxis yAxisId="left" allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip labelFormatter={formatDate} />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="total_orders"
                  stroke="#6366F1"
                  dot={false}
                  strokeWidth={2}
                  name="Orders"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="avg_bump_time_minutes"
                  stroke="#0EA5E9"
                  dot={false}
                  strokeWidth={2}
                  name="Avg Bump (min)"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className={EMPTY_CLASS}>No data available for overlay trend.</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Total Orders Line Chart */}
          <div className={CARD_CLASS}>
            <h4 className={TITLE_CLASS}>Orders Trend</h4>
            {ordersByDay.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={ordersByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    ticks={trendTicks}
                    tickFormatter={formatMonthDay}
                    minTickGap={30}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip labelFormatter={formatDate} />
                  <Legend />
                  <Line type="monotone" dataKey="total_orders" stroke="#8884d8" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className={EMPTY_CLASS}>No data available for orders.</p>
            )}
          </div>

          {/* Avg Bump Time Trend Chart */}
          <div className={CARD_CLASS}>
            <h4 className={TITLE_CLASS}>Avg. Ticket Bump Time Trend</h4>
            {avgBumpTimeByDay.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={avgBumpTimeByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    ticks={bumpTrendTicks}
                    tickFormatter={formatMonthDay}
                    minTickGap={30}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip labelFormatter={formatDate} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="avg_bump_time_minutes"
                    stroke="#4A90E2"
                    dot={false}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className={EMPTY_CLASS}>No daily bump-time trend data available.</p>
            )}
          </div>
        </div>

        {/* Peak Hour Heatmap */}
        <div className={CARD_CLASS}>
          <h4 className={TITLE_CLASS}>Peak Hour Heatmap (Orders)</h4>
          {ordersHeatmap.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="min-w-[920px]">
                <div className="grid" style={{ gridTemplateColumns: '80px repeat(24, minmax(24px, 1fr))' }}>
                  <div />
                  {Array.from({ length: 24 }, (_, h) => (
                    <div key={`hour-${h}`} className="text-[10px] text-gray-500 dark:text-gray-400 text-center mb-1">
                      {formatHour12(h)}
                    </div>
                  ))}

                  {WEEKDAYS.map((day, dIdx) => (
                    <React.Fragment key={`row-${day}`}>
                      <div className="text-xs text-gray-700 dark:text-gray-300 pr-2 py-1">{day}</div>
                      {Array.from({ length: 24 }, (_, h) => {
                        const count = Number(heatmapStats.keyToCount.get(`${dIdx}-${h}`) || 0);
                        return (
                          <div
                            key={`${day}-${h}`}
                            className="h-6 border border-white dark:border-gray-800"
                            style={{ backgroundColor: heatColor(count) }}
                            title={renderHeatCellTooltip(day, h, count)}
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className={EMPTY_CLASS}>No data available for peak-hour heatmap.</p>
          )}
        </div>

        {/* Day-of-Week Trend Lines */}
        <div className={CARD_CLASS}>
          <h4 className={TITLE_CLASS}>Day-of-Week Trend Lines</h4>
          {dayOfWeekTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={dayOfWeekTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="week_start"
                  ticks={dayOfWeekTicks}
                  tickFormatter={formatMonthDay}
                  minTickGap={30}
                />
                <YAxis allowDecimals={false} />
                <Tooltip labelFormatter={(v) => `Week of ${formatDate(v)}`} />
                <Legend />
                {WEEKDAYS.map((day, index) => (
                  <Line
                    key={day}
                    type="monotone"
                    dataKey={day}
                    name={day}
                    stroke={WEEKDAY_COLORS[index]}
                    dot={false}
                    strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className={EMPTY_CLASS}>No data available for day-of-week trends.</p>
          )}
        </div>

        {/* Item Share Pie */}
        <div className={CARD_CLASS}>
          <h4 className={TITLE_CLASS}>Items Share by Total Ordered</h4>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={450}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={150}
                  label={renderPieLabel}
                  labelLine={renderPieLabelLine}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={renderPieTooltip} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className={EMPTY_CLASS}>No item totals available for pie chart.</p>
          )}
        </div>

        {/* Item Scatter */}
        <div className={CARD_CLASS}>
          <h4 className={TITLE_CLASS}>Items: Times Ordered vs Avg Bump Time</h4>
          {scatterData.length > 0 ? (
            <ResponsiveContainer width="100%" height={360}>
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid />
                <XAxis type="number" dataKey="total_quantity" name="Times Ordered">
                  <Label value="Times Ordered" position="insideBottom" offset={-5} />
                </XAxis>
                <YAxis type="number" dataKey="avg_bump_minutes" name="Avg Bump (min)">
                  <Label value="Avg Bump (min)" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
                </YAxis>
                <Tooltip content={renderScatterTooltip} />
                <Scatter data={scatterData} fill="#2563EB" />
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <p className={EMPTY_CLASS}>No item bump-time data available for scatter chart.</p>
          )}
        </div>
      </div>

    );
  }

  return null;
};

export default ChartsSection;
